/*
 * Modal de detalle de una pieza de la Matriz por pieza: el porqué de la
 * recomendación (extraído del modal que ya existía en AgenteMarketing.js) más
 * los botones para ejecutarla de verdad — escalar, pausar, optimizar y
 * reactivar. Solo Marketing ve los botones; Operaciones (y cualquier otro rol)
 * ve el mismo detalle, sin ninguna acción.
 *
 * "Escalar" y "Pausar" pegan a /aprobar-y-ejecutar: un solo clic que por dentro
 * aprueba la recomendación y la ejecuta en Meta, con su confirmación exacta
 * (monto o pieza) antes de tocar algo real.
 * "Optimizar" no tiene acción de API en Meta (no existe un botón "optimizar" en
 * Ads Manager): abre el editor de pieza para hacer el cambio de audiencia/
 * creativo a mano.
 * "Reactivar" solo aparece si la pieza está pausada en Meta.
 */
import React from 'react'

const money = value => value === null || value === undefined ? '—' : `S/ ${Number(value).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`
const number = value => value === null || value === undefined ? '—' : Number(value).toLocaleString('es-PE')
const percent = value => value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`
const label = value => String(value || '').replace(/_/g,' ')
const stateLabel = value => ({
  pendiente_marketing:'Pendiente de revisión de Marketing', pendiente_operaciones:'Pendiente de aprobación de Operaciones',
  pendiente_ejecucion_manual:'Aprobado para ejecución manual', ejecutada:'Ejecución registrada',
  verificada:'Ejecución verificada', superada:'Superada', rechazada:'Rechazada'
}[value] || 'Recomendado')

function Badge({value}) { return <span className={`am-badge am-badge-${value}`}>{label(value)}</span> }

const PiezaDetalleModal = ({ pieza, isMarketing, accionEnCurso, onClose, onAprobarYEjecutar, onReactivar, onOptimizar, onRegistrarEjecucion }) => {
  if (!pieza) return null

  const esMeta = pieza.plataforma === 'facebook'
  // Mismo requisito que exige el backend para el clic de un paso: la
  // recomendación tiene que estar recien calculada (pendiente_marketing), no
  // ya aprobada ni ejecutada.
  const tienePendiente = pieza.id_recomendacion && pieza.estado_revision === 'pendiente_marketing'
  const puedeEscalar = isMarketing && esMeta && tienePendiente && pieza.recomendacion === 'escalar' && pieza.presupuesto_propuesto != null
  const puedePausar = isMarketing && esMeta && tienePendiente && pieza.recomendacion === 'pausar'
  const puedeOptimizar = isMarketing && pieza.recomendacion === 'optimizar'
  const puedeReactivar = isMarketing && esMeta && Number(pieza.activa) === 0
  // Ya aprobada, esperando ejecutarse a mano (p.ej. otra plataforma o quedó pendiente).
  const puedeRegistrarEjecucion = isMarketing && pieza.id_recomendacion && pieza.estado_revision === 'pendiente_ejecucion_manual'
  const yaEjecutada = ['ejecutada','verificada'].includes(pieza.estado_revision)

  return <div className="am-modal-backdrop" onClick={onClose}>
    <div className="am-modal-card" onClick={e=>e.stopPropagation()}>
      <div className="d-flex justify-content-between">
        <h5>{pieza.nombre}</h5>
        <button className="close" onClick={onClose}>&times;</button>
      </div>
      <p><Badge value={pieza.recomendacion}/> <span className="ml-2">{stateLabel(pieza.estado_revision)}</span>
        <span className={`am-badge ml-2 ${Number(pieza.activa)===1?'am-estado-activa':'am-estado-pausada'}`}>{Number(pieza.activa)===1?'Activa en Meta':'Pausada en Meta'}</span>
      </p>

      <div className="am-detail">
        <strong>Por qué:</strong> {pieza.detalle?.explicacion || pieza.causa || 'Sin explicación registrada.'}
        {pieza.detalle?.objetivos && <small className="d-block text-muted mt-1">
          Objetivo CPA {money(pieza.detalle.objetivos.cpa)} · CPL {money(pieza.detalle.objetivos.cpl)} · calidad mínima {percent(pieza.detalle.objetivos.calidad_minima_pct)}
        </small>}
      </div>

      <div className="am-detail mt-2">
        <div><strong>Evidencia:</strong> {money(pieza.gasto_acumulado)} / piso {money(pieza.evidencia_umbral)} · {Number(pieza.evidencia_suficiente)===1?'suficiente':'insuficiente'}</div>
        <div><strong>Costo:</strong> {pieza.metrica_costo?.toUpperCase()} · {pieza.nivel_costo} · {pieza.metrica_costo==='cpa'?money(pieza.cpa):money(pieza.cpl)}</div>
        <div><strong>Calidad:</strong> {percent(pieza.trato_pct)} frente a 5%</div>
        {Number(pieza.cero_forzado)===1 && <div className="text-danger"><strong>Cero forzado:</strong> evidencia suficiente y cero tratos.</div>}
        {pieza.recomendacion==='escalar' && pieza.presupuesto_propuesto!=null && <div><strong>Propuesta:</strong> de {money(pieza.presupuesto_actual)} a {money(pieza.presupuesto_propuesto)}</div>}
        <hr/>
        <small className="d-block">{pieza.detalle?.fecha} · {pieza.plataforma}{pieza.nombre_grupo?` · ${pieza.nombre_grupo}`:''} · regla v{pieza.detalle?.version_regla} · CRM {pieza.detalle?.fuente_crm}</small>
      </div>

      <p className="text-muted mt-2 mb-3">CTR {percent(pieza.ctr)} · clics {number(pieza.clics)} · reproducciones {number(pieza.reproducciones)}. Son métricas informativas y no cambian la recomendación.</p>

      {!isMarketing && <p className="text-muted mb-0"><small>Solo Marketing puede ejecutar acciones sobre esta pieza.</small></p>}

      {isMarketing && <div className="d-flex flex-wrap" style={{gap:8}}>
        {puedeEscalar && <button className="btn btn-success" disabled={accionEnCurso} onClick={()=>onAprobarYEjecutar(pieza)}>{accionEnCurso?'Ejecutando…':`▲ Escalar a ${money(pieza.presupuesto_propuesto)}`}</button>}
        {puedePausar && <button className="btn btn-danger" disabled={accionEnCurso} onClick={()=>onAprobarYEjecutar(pieza)}>{accionEnCurso?'Ejecutando…':'⏸ Pausar en Meta'}</button>}
        {puedeOptimizar && <button className="btn btn-outline-primary" onClick={()=>onOptimizar(pieza)}>✏️ Optimizar (editar pieza)</button>}
        {puedeReactivar && <button className="btn btn-outline-success" disabled={accionEnCurso} onClick={()=>onReactivar(pieza)}>{accionEnCurso?'Ejecutando…':'▶ Reactivar en Meta'}</button>}
        {puedeRegistrarEjecucion && <button className="btn btn-outline-primary" onClick={()=>onRegistrarEjecucion(pieza)}>Registrar ejecución y evidencia</button>}
        {yaEjecutada && !puedeReactivar && <span className="text-muted align-self-center">Ya ejecutada — ver Historial para el detalle.</span>}
      </div>}
    </div>
  </div>
}

export default PiezaDetalleModal
